import * as AppleAuthentication from "expo-apple-authentication";
import { AppleAuthenticationButtonType } from "expo-apple-authentication";
import type { CodedError } from "expo-modules-core";
import { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import { Radius, Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { randomString } from "~/util/string";

export type AppleCredentialResponse = {
    user: string;
    code: string | null;
    token: string | null;
    email: string | null;
    name: string | null;
};

export type AppleLoginButtonProps = {
    type?: AppleAuthentication.AppleAuthenticationButtonType;
    disabled?: boolean;
    loading?: boolean;
    onAuthenticationStart?: () => void;
    onAuthenticationSuccess: (credential: AppleCredentialResponse) => Promise<void>;
    onAuthenticationError?: (errors: string[]) => void;
    onAuthenticationCancelled?: () => void;
    onAuthenticationFailed?: () => void;
    onAuthenticationResolved?: () => void;
};

export const AppleLoginButton = ({
    disabled,
    loading,
    onAuthenticationStart,
    onAuthenticationSuccess,
    onAuthenticationError,
    onAuthenticationCancelled,
    onAuthenticationResolved,
    onAuthenticationFailed,
    type = AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN,
}: AppleLoginButtonProps) => {
    const { dark } = useTheme();
    const [available, setAvailable] = useState(false);

    useEffect(() => {
        AppleAuthentication.isAvailableAsync().then(setAvailable);
    }, []);

    const onPress = async () => {
        if (disabled || !available) {
            return;
        }

        onAuthenticationStart?.();

        const state = randomString();

        AppleAuthentication.signInAsync({
            requestedScopes: [
                AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
                AppleAuthentication.AppleAuthenticationScope.EMAIL,
            ],
            state,
        })
            .then(async (credential) => {
                if (state === credential.state) {
                    await onAuthenticationSuccess({
                        user: credential.user,
                        code: credential.authorizationCode,
                        token: credential.identityToken,
                        email: credential.email,
                        name: credential.fullName && AppleAuthentication.formatFullName(credential.fullName),
                    });
                } else {
                    onAuthenticationError?.(["Invalid login request."]);
                }
            })
            .catch((e: CodedError) => {
                if (e.code === "ERR_REQUEST_CANCELED") {
                    onAuthenticationCancelled?.();
                } else {
                    onAuthenticationError?.([e.message]);
                }
                onAuthenticationFailed?.();
            })
            .finally(() => {
                onAuthenticationResolved?.();
            });
    };

    return (
        <View style={styles.container}>
            {available &&
                (disabled || loading ? (
                    <Button
                        disabled
                        icon="apple"
                        size="lg"
                        iconSize={Space.sm}
                        loading={loading}
                        title={
                            type === AppleAuthenticationButtonType.SIGN_UP ? "Sign up with Apple" : "Sign in with Apple"
                        }
                    />
                ) : (
                    <AppleAuthentication.AppleAuthenticationButton
                        buttonType={type}
                        buttonStyle={
                            dark
                                ? AppleAuthentication.AppleAuthenticationButtonStyle.WHITE
                                : AppleAuthentication.AppleAuthenticationButtonStyle.BLACK
                        }
                        style={styles.button}
                        cornerRadius={Radius.md}
                        aria-disabled={disabled}
                        onPress={onPress}
                    />
                ))}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: "stretch",
        justifyContent: "flex-start",
        height: Space["12"],
        width: "100%",
    },
    button: {
        height: Space["12"],
        width: "100%",
    },
});
